import { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Database,
  Layers,
  Plus,
  Loader2,
  AlertCircle,
  Edit2,
  Trash2,
  Search,
  Filter,
  X,
} from 'lucide-react';
import api, { extractError } from '../lib/api';
import { useAuth } from '../lib/auth';
import type { DataField, PaginatedResponse, Organization, Cluster } from '../types';
import { AggregationMode, ResetInterval } from '../types';
import AiBotWidget from '../components/ai-bot/AiBotWidget';

const fieldTypeLabels: Record<string, string> = {
  POI_LIST: "POI-Liste",
  POI: "POI",
  COORDINATE_PAIR: "Koordinaten (Paar)",
  COORDINATE: "Koordinaten (Punkt)",
  TEXT: "Text",
  NUMBER: "Zahl",
  BOOLEAN: "Ja/Nein",
  DATE: "Datum",
  DATETIME: "Datum & Zeit",
  SELECT: "Auswahl",
  MULTISELECT: "Mehrfachauswahl",
  FILE: "Datei",
  IMAGE: "Bild",
  GEO_POINT: "Geo-Punkt",
  GEO_SHAPE: "Geo-Form",
  GEOJSON: "GeoJSON (Punkte, Linien, Polygone)",
};

const fieldTypeColors: Record<string, string> = {
  POI_LIST: "badge-accent-violet",
  POI: "badge-accent-violet",
  COORDINATE_PAIR: "badge-accent-orange",
  COORDINATE: "badge-accent-orange",
  TEXT: "badge-info",
  NUMBER: "badge-success",
  BOOLEAN: "badge-warning",
  DATE: "badge-primary",
  DATETIME: "badge-primary",
  SELECT: "badge-info",
  MULTISELECT: "badge-info",
  FILE: "badge-danger",
  IMAGE: "badge-danger",
  GEO_POINT: "badge-accent-teal",
  GEO_SHAPE: "badge-accent-teal",
  GEOJSON: "badge-accent-pink",
};

const resetIntervalLabels: Record<string, string> = {
  NEVER: "Nie",
  DAILY: "Täglich",
  WEEKLY: "Wöchentlich",
  MONTHLY: "Monatlich",
  YEARLY: "Jährlich",
};

interface FieldFormModalProps {
  isOpen: boolean;
  onClose: () => void;
  editField?: DataField | null;
}

function FieldFormModal({ isOpen, onClose, editField }: FieldFormModalProps) {
  const queryClient = useQueryClient();
  const [formData, setFormData] = useState({
    name: editField?.name ?? '',
    key: editField?.key ?? '',
    label: editField?.label ?? '',
    description: editField?.description ?? '',
    type: editField ? editField.type : 'TEXT',
    organizationId: editField?.organizationId ?? '',
    isRequired: editField?.isRequired ?? false,
    hasPersonReference: editField?.hasPersonReference ?? false,
    isSensitive: editField?.isSensitive ?? false,
    isUnique: editField?.isUnique ?? false,
    sortOrder: editField?.sortOrder ?? 0,
    options: editField?.options?.join('\n') ?? '',
    aggregationMode: editField?.aggregationMode ?? 'OVERWRITE',
    resetInterval: editField?.resetInterval ?? 'NEVER',
    validationPattern: editField?.validationPattern ?? '',
    plausibilityCheck: editField?.plausibilityCheck ?? false,
    clusterIds: (editField?.clusters?.map((entry) => entry.clusterId) ?? []) as string[],
  });
  const [templates, setTemplates] = useState<any[]>([]);
  const [saveTemplate, setSaveTemplate] = useState<{name:string, pattern:string} | null>(null);
  const [error, setError] = useState<string | null>(null);
  // Native selects können Optionen nicht einfärben – der Punkt neben dem Select
  // zeigt daher die Farbe des zuletzt gewählten Clusters (die Auswahl wird
  // direkt als Chip übernommen und das Select zurückgesetzt).
  const [lastClusterPickId, setLastClusterPickId] = useState('');

  const { data: orgsData } = useQuery<PaginatedResponse<Organization>>({
    queryKey: ['organizations'],
    queryFn: async () => {
      const res = await api.get('/organizations', { params: { limit: 200 } });
      return res.data;
    },
  });

  const { data: clustersData } = useQuery<Cluster[]>({
    queryKey: ['clusters'],
    queryFn: async () => {
      const res = await api.get('/clusters');
      return Array.isArray(res.data) ? res.data : (res.data?.data ?? []);
    },
  });
  const clusters = clustersData ?? [];

  const addCluster = (clusterId: string) => {
    if (formData.clusterIds.includes(clusterId)) return;
    setFormData(prev => ({ ...prev, clusterIds: [...prev.clusterIds, clusterId] }));
  };
  const removeCluster = (clusterId: string) => {
    setFormData(prev => ({ ...prev, clusterIds: prev.clusterIds.filter(id => id !== clusterId) }));
  };

  const createMutation = useMutation({
    mutationFn: async (data: typeof formData) => {
      const payload = {
        ...data,
        validationPattern: data.validationPattern || undefined,
        resetInterval: data.resetInterval,
        plausibilityCheck: data.plausibilityCheck || undefined,
        options: ['SELECT', 'MULTISELECT'].includes(data.type)
          ? data.options.split('\n').filter(Boolean)
          : null,
      };
      const res = await api.post('/data-fields', payload);
      return res.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['fields'] });
      onClose();
    },
    onError: (err) => setError(extractError(err).message),
  });

  const updateMutation = useMutation({
    mutationFn: async (data: typeof formData) => {
      const payload = {
        ...data,
        validationPattern: data.validationPattern || undefined,
        resetInterval: data.resetInterval,
        plausibilityCheck: data.plausibilityCheck || undefined,
        options: ['SELECT', 'MULTISELECT'].includes(data.type)
          ? data.options.split('\n').filter(Boolean)
          : null,
      };
      const res = await api.patch(`/data-fields/${editField!.id}`, payload);
      return res.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['fields'] });
      onClose();
    },
    onError: (err) => setError(extractError(err).message),
  });

  // Load templates on mount
  useEffect(() => { api.get("/data-fields/templates/list").then(res => setTemplates(res.data || [])).catch(() => {}); }, []);

  // Save template
  const _st = saveTemplate;
  useEffect(() => {
    if (_st && _st.name && _st.pattern) {
      api.post("/data-fields/templates", _st).then(() => {
        api.get("/data-fields/templates/list").then(res => setTemplates(res.data || []));
        setSaveTemplate(null);
      }).catch((e: any) => alert("Fehler: " + (e.response?.data?.message || e.message)));
    }
  }, [_st]);

  if (!isOpen) return null;

  const isSubmitting = createMutation.isPending || updateMutation.isPending;

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    if (editField) {
      updateMutation.mutate(formData);
    } else {
      createMutation.mutate(formData);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
      <div className="w-full max-w-2xl rounded-xl bg-white shadow-xl max-h-[90vh] overflow-y-auto">
        <div className="border-b border-gray-200 px-6 py-4">
          <h2 className="text-lg font-semibold text-gray-900">
            {editField ? 'Datenfeld bearbeiten' : 'Neues Datenfeld'}
          </h2>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          {error && (
            <div className="flex items-center gap-2 rounded-lg bg-red-50 border border-red-200 px-4 py-3 text-sm text-red-700">
              <AlertCircle size={18} className="shrink-0" />
              <span>{error}</span>
            </div>
          )}

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label htmlFor="field-name" className="label">Name</label>
              <input id="field-name" type="text" className="input-field" required
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })} />
            </div>
            <div>
              <label htmlFor="field-key" className="label">Schlüssel</label>
              <input id="field-key" type="text" className="input-field" required
                value={formData.key}
                onChange={(e) => setFormData({ ...formData, key: e.target.value })} />
            </div>
          </div>

          <div>
            <label htmlFor="field-label" className="label">Anzeigename</label>
            <input id="field-label" type="text" className="input-field" required
              value={formData.label}
              onChange={(e) => setFormData({ ...formData, label: e.target.value })} />
          </div>

          <div>
            <label htmlFor="field-desc" className="label">Beschreibung</label>
            <textarea id="field-desc" className="input-field" rows={2}
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })} />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label htmlFor="field-type" className="label">Feldtyp</label>
              <select id="field-type" className="input-field"
                value={formData.type}
                onChange={(e) => setFormData({ ...formData, type: e.target.value  })}>
                {Object.entries(fieldTypeLabels).map(([value, label]) => (
                  <option key={value} value={value}>{label}</option>
                ))}
              </select>
            </div>
            <div>
              <label htmlFor="field-org" className="label">Organisation</label>
              <select id="field-org" className="input-field" required
                value={formData.organizationId}
                onChange={(e) => setFormData({ ...formData, organizationId: e.target.value })}>
                <option value="">Bitte wählen</option>
                {(Array.isArray(orgsData) ? orgsData : (orgsData?.data ?? [])).map((org) => (
                  <option key={org.id} value={org.id}>{org.name}</option>
                ))}
              </select>
            </div>
          </div>

          <div>
            <label className="label">Cluster (Pakete)</label>
            {formData.clusterIds.length > 0 && (
              <div className="flex flex-wrap gap-1 mb-2">
                {formData.clusterIds.map(clusterId => {
                  const cluster = clusters.find(c => c.id === clusterId);
                  return (
                    <span key={clusterId} className="inline-flex items-center gap-1 bg-primary-50 text-primary-800 text-xs font-medium px-2 py-1 rounded-full">
                      <span
                        className="h-3 w-3 shrink-0 rounded-full border border-gray-200 ring-1 ring-black/10"
                        style={{ backgroundColor: cluster?.color || '#9ca3af' }}
                        title={cluster?.name}
                        aria-hidden="true"
                      />
                      {cluster?.name || clusterId}
                      <button type="button" onClick={() => removeCluster(clusterId)} className="hover:text-red-500"><X size={12} /></button>
                    </span>
                  );
                })}
              </div>
            )}
            <div className="flex items-center gap-2">
              <select
                className="input-field flex-1"
                value=""
                onChange={(e) => { if (e.target.value) { setLastClusterPickId(e.target.value); addCluster(e.target.value); e.target.value = ''; } }}
              >
                <option value="">Cluster auswählen...</option>
                {clusters.filter(c => !formData.clusterIds.includes(c.id)).map(cluster => (
                  <option key={cluster.id} value={cluster.id}>{cluster.name}</option>
                ))}
              </select>
              {(() => {
                const picked = clusters.find(c => c.id === lastClusterPickId);
                return picked && formData.clusterIds.includes(lastClusterPickId) ? (
                  <span
                    className="h-3 w-3 shrink-0 rounded-full border border-gray-200 ring-1 ring-black/10"
                    style={{ backgroundColor: picked.color || '#9ca3af' }}
                    title={picked.name}
                    aria-hidden="true"
                  />
                ) : null;
              })()}
            </div>
          </div>

          {['SELECT', 'MULTISELECT'].includes(formData.type) && (
            <div>
              <label htmlFor="field-options" className="label">Optionen (eine pro Zeile)</label>
              <textarea id="field-options" className="input-field" rows={4}
                value={formData.options}
                onChange={(e) => setFormData({ ...formData, options: e.target.value })}
                placeholder="Option 1&#10;Option 2&#10;Option 3" />
            </div>
          )}

          <div className="grid grid-cols-3 gap-4">
            <div>
              <label htmlFor="field-aggr" className="label">Aggregation</label>
              <select id="field-aggr" className="input-field"
                value={formData.aggregationMode}
                onChange={(e) => setFormData({ ...formData, aggregationMode: e.target.value as AggregationMode })}>
                {Object.entries(AggregationMode).map(([key, value]) => (
                  <option key={key} value={value}>{key}</option>
                ))}
              </select>
              <label className="label mt-3">Reset-Intervall</label>
              <select className="input-field"
                value={formData.resetInterval}
                onChange={(e) => setFormData({ ...formData, resetInterval: e.target.value as ResetInterval })}>
                {Object.entries(ResetInterval).map(([key, value]) => (
                  <option key={key} value={value}>{resetIntervalLabels[key]}</option>
                ))}
              </select>
            </div>
            <div>
              <div className="border-t border-gray-100 pt-4">
              <h3 className="text-sm font-semibold text-gray-700 mb-3">Validierung</h3>
              <label className="label">Prüfungs-Vorlage</label>
              <select className="input-field" value={formData.validationPattern} onChange={(e) => setFormData({ ...formData, validationPattern: e.target.value })}>
                <option value="">— Keine / Manuell —</option>
                {(templates || []).map((t: any) => (
                  <option key={t.id} value={t.pattern}>{t.name}</option>
                ))}
              </select>
              <label className="label mt-3">Regex-Pattern</label>
              <div className="flex gap-2">
                <input type="text" className="input-field flex-1" value={formData.validationPattern}
                  onChange={(e) => setFormData({ ...formData, validationPattern: e.target.value })}
                  placeholder="z.B. ^[0-9]{4}$" />
                <button type="button" onClick={() => {
                  const name = prompt("Name der Vorlage:");
                  if (name && formData.validationPattern) setSaveTemplate({ name, pattern: formData.validationPattern });
                }} className="btn-secondary text-xs px-2">+</button>
              </div>
              <label className="flex items-center gap-2 mt-3 cursor-pointer">
                <input type="checkbox" className="h-4 w-4" checked={formData.plausibilityCheck}
                  onChange={(e) => setFormData({ ...formData, plausibilityCheck: e.target.checked })} />
                <span className="text-sm text-gray-700">Plausibilitätsprüfung aktivieren</span>
              </label>
            </div>
            <label htmlFor="field-sort" className="label">Sortierung</label>
              <input id="field-sort" type="number" className="input-field"
                value={formData.sortOrder}
                onChange={(e) => setFormData({ ...formData, sortOrder: parseInt(e.target.value) || 0 })} />
            </div>
          </div>

          <div className="flex items-center gap-6">
            <label className="flex items-center gap-2">
              <input type="checkbox"
                className="h-4 w-4 rounded border-gray-300 text-primary focus:ring-primary"
                checked={formData.isRequired}
                onChange={(e) => setFormData({ ...formData, isRequired: e.target.checked })} />
              <span className="text-sm text-gray-700">Pflichtfeld</span>
            </label>
            <label className="flex items-center gap-2">
              <input type="checkbox"
                className="h-4 w-4 rounded border-gray-300 text-primary focus:ring-primary"
                checked={formData.isUnique}
                onChange={(e) => setFormData({ ...formData, isUnique: e.target.checked })} />
              <span className="text-sm text-gray-700">Eindeutig</span>
            </label>
          </div>

          <div className="border-t border-gray-200 pt-4">
            <p className="text-xs font-medium text-gray-500 uppercase tracking-wider mb-3">Datenschutz-Klassifizierung</p>
            <div className="flex items-center gap-6">
              <label className="flex items-center gap-2">
                <input type="checkbox"
                  className="h-4 w-4 rounded border-gray-300 text-orange-500 focus:ring-orange-500"
                  checked={formData.hasPersonReference}
                  onChange={(e) => setFormData({ ...formData, hasPersonReference: e.target.checked })} />
                <span className="text-sm text-gray-700">Personenbezogene Daten</span>
              </label>
              <label className="flex items-center gap-2">
                <input type="checkbox"
                  className="h-4 w-4 rounded border-gray-300 text-red-500 focus:ring-red-500"
                  checked={formData.isSensitive}
                  onChange={(e) => setFormData({ ...formData, isSensitive: e.target.checked })} />
                <span className="text-sm text-gray-700">Sensibel (besonders schützenswert)</span>
              </label>
            </div>
          </div>

          <div className="flex justify-end gap-3 pt-2">
            <button type="button" onClick={onClose} className="btn-secondary">Abbrechen</button>
            <button type="submit" disabled={isSubmitting} className="btn-primary">
              {isSubmitting ? (
                <><Loader2 size={16} className="animate-spin" /> Speichern...</>
              ) : (
                editField ? 'Aktualisieren' : 'Erstellen'
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default function DataFieldsPage() {
  const queryClient = useQueryClient();
  const { user } = useAuth();
  const { data: orgsPage } = useQuery<any>({ queryKey: ['organizations-page'], queryFn: async () => { const res = await api.get('/organizations', { params: { limit: 200 } }); return res.data; } });
  const [showModal, setShowModal] = useState(false);
  const [editField, setEditField] = useState<DataField | null>(null);
  const [formVersion, setFormVersion] = useState(0);
  const [searchQuery, setSearchQuery] = useState('');
  const [typeFilter, setTypeFilter] = useState<string>('');
  const [clusterFilter, setClusterFilter] = useState<string>('');

  // Reiter und Cluster-Verwaltung
  const [activeTab, setActiveTab] = useState<'fields' | 'clusters'>('fields');
  const [editingClusterId, setEditingClusterId] = useState<string | null>(null);
  const [clusterError, setClusterError] = useState<string | null>(null);
  const [clusterForm, setClusterForm] = useState({
    name: '',
    description: '',
    color: '#B70E0C',
    visibleToChildren: false,
  });

  const { data: clustersData, isLoading: clustersLoading } = useQuery<Cluster[]>({
    queryKey: ['clusters'],
    queryFn: async () => {
      const res = await api.get('/clusters');
      return Array.isArray(res.data) ? res.data : (res.data?.data ?? []);
    },
  });
  const clusters = clustersData ?? [];
  const ownOrganizationId = user?.organizationId ?? null;
  const clusterColor = (value?: string | null) =>
    /^#[0-9a-fA-F]{6}$/.test(value ?? '') ? (value as string) : '#B70E0C';
  const isOwnCluster = (cluster: Cluster) =>
    (cluster.organizationId ?? null) === ownOrganizationId;

  const { data, isLoading, error } = useQuery<DataField[]>({
    queryKey: ['fields'],
    queryFn: async () => {
      const res = await api.get('/data-fields', { params: { limit: 100 } });
      const items = Array.isArray(res.data) ? res.data : (res.data?.data ?? []);
      return items;
    },
  });

  const deleteMutation = useMutation({
    mutationFn: async (id: string) => {
      await api.delete(`/data-fields/${id}`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['fields'] });
    },
  });

  // ─── Cluster-Verwaltung ─────────────────────────────

  function resetClusterForm() {
    setEditingClusterId(null);
    setClusterForm({ name: '', description: '', color: '#B70E0C', visibleToChildren: false });
    setClusterError(null);
  }

  const saveClusterMutation = useMutation({
    mutationFn: async () => {
      const payload = {
        name: clusterForm.name,
        description: clusterForm.description || undefined,
        color: clusterForm.color,
        visibleToChildren: clusterForm.visibleToChildren,
      };
      if (editingClusterId) {
        const res = await api.patch(`/clusters/${editingClusterId}`, payload);
        return res.data;
      }
      const res = await api.post('/clusters', payload);
      return res.data;
    },
    onSuccess: () => {
      resetClusterForm();
      queryClient.invalidateQueries({ queryKey: ['clusters'] });
    },
    onError: (err) => setClusterError(extractError(err).message),
  });

  const deleteClusterMutation = useMutation({
    mutationFn: async (id: string) => {
      await api.delete(`/clusters/${id}`);
    },
    onSuccess: (_data, id) => {
      if (editingClusterId === id) resetClusterForm();
      queryClient.invalidateQueries({ queryKey: ['clusters'] });
    },
    onError: (err) => setClusterError(extractError(err).message),
  });

  function handleClusterSubmit(e: React.FormEvent) {
    e.preventDefault();
    setClusterError(null);
    saveClusterMutation.mutate();
  }

  function openClusterEdit(cluster: Cluster) {
    setEditingClusterId(cluster.id);
    setClusterForm({
      name: cluster.name,
      description: cluster.description ?? '',
      color: clusterColor(cluster.color),
      visibleToChildren: cluster.visibleToChildren ?? false,
    });
    setClusterError(null);
  }

  function handleClusterDelete(cluster: Cluster) {
    if (window.confirm(`Cluster "${cluster.name}" wirklich löschen?`)) {
      deleteClusterMutation.mutate(cluster.id);
    }
  }

  const fieldsList = data ?? [];
  const filteredFields = fieldsList.filter((field) => {
    const searchLower = searchQuery.toLowerCase();
    const matchesSearch = !searchQuery || 
      (field.name && field.name.toLowerCase().includes(searchLower)) ||
      (field as any).label?.toLowerCase().includes(searchLower) ||
      (field as any).key?.toLowerCase().includes(searchLower);
    const matchesType = !typeFilter || field.type === typeFilter;
    const matchesCluster =
      !clusterFilter ||
      (clusterFilter === '__none__'
        ? (field.clusters ?? []).length === 0
        : (field.clusters ?? []).some(entry => entry.clusterId === clusterFilter));
    return matchesSearch && matchesType && matchesCluster;
  });

  const handleOpenCreate = () => {
    setEditField(null);
    setFormVersion((version) => version + 1);
    setShowModal(true);
  };

  const handleEdit = (field: DataField) => {
    setEditField(field);
    setFormVersion((version) => version + 1);
    setShowModal(true);
  };

  /**
   * Der KI-Bot hat den Datensatz bereits angelegt. Das Anlege-Formular wird
   * geschlossen und geleert – es gibt keinen vorbefüllten Zustand mehr, der
   * versehentlich ein zweites Mal abgeschickt werden könnte. Die Liste wird
   * sofort neu geladen, damit der neue Eintrag sichtbar ist.
   */
  const handleBotCreated = () => {
    setShowModal(false);
    setEditField(null);
    setFormVersion((version) => version + 1);
    queryClient.invalidateQueries({ queryKey: ['fields'] });
    queryClient.invalidateQueries({ queryKey: ['clusters'] });
  };

  const handleDelete = (field: DataField) => {
    if (window.confirm(`Datenfeld "${field.name}" wirklich löschen?`)) {
      deleteMutation.mutate(field.id);
    }
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setEditField(null);
  };

  return (
    <div className="space-y-6">
      {/* Reiter */}
      <div className="flex items-center gap-1 border-b border-gray-200">
        <button
          onClick={() => setActiveTab('fields')}
          className={`px-4 py-2.5 text-sm font-medium rounded-t-lg transition-colors ${
            activeTab === 'fields'
              ? 'bg-white text-primary border border-b-white border-gray-200 -mb-px'
              : 'text-gray-500 hover:text-gray-700'
          }`}
        >
          <Database size={16} className="inline mr-1.5" />Datenfelder
        </button>
        <button
          onClick={() => setActiveTab('clusters')}
          className={`px-4 py-2.5 text-sm font-medium rounded-t-lg transition-colors ${
            activeTab === 'clusters'
              ? 'bg-white text-primary border border-b-white border-gray-200 -mb-px'
              : 'text-gray-500 hover:text-gray-700'
          }`}
        >
          <Layers size={16} className="inline mr-1.5" />Cluster
        </button>
      </div>

      {activeTab === 'fields' ? (
        <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Database size={28} className="text-primary" />
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Datenfelder</h1>
            <p className="text-sm text-gray-500">Definition und Verwaltung von Datenfeldern</p>
          </div>
        </div>
        <button onClick={handleOpenCreate} className="btn-primary">
          <Plus size={18} /> Neues Datenfeld
        </button>
      </div>

      <div className="flex flex-wrap gap-3">
        <div className="relative flex-1 max-w-sm">
          <Search size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
          <input type="text" placeholder="Datenfeld suchen..." className="input-field pl-10"
            value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)} />
        </div>
        <div className="relative">
          <Filter size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
          <select className="input-field pl-10"
            value={typeFilter}
            onChange={(e) => setTypeFilter(e.target.value)}>
            <option value="">Alle Typen</option>
            {Object.entries(fieldTypeLabels).map(([value, label]) => (
              <option key={value} value={value}>{label}</option>
            ))}
          </select>
        </div>
        <div className="relative flex items-center gap-2">
          <Filter size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
          <select className="input-field pl-10"
            value={clusterFilter}
            onChange={(e) => setClusterFilter(e.target.value)}>
            <option value="">Alle Cluster</option>
            <option value="__none__">Ohne Cluster</option>
            {clusters.map((cluster) => (
              <option key={cluster.id} value={cluster.id}>{cluster.name}</option>
            ))}
          </select>
          {(() => {
            const selected = clusterFilter && clusterFilter !== '__none__'
              ? clusters.find((cluster) => cluster.id === clusterFilter)
              : undefined;
            return selected ? (
              <span
                className="h-3 w-3 shrink-0 rounded-full border border-gray-200 ring-1 ring-black/10"
                style={{ backgroundColor: selected.color || '#9ca3af' }}
                title={selected.name}
                aria-hidden="true"
              />
            ) : null;
          })()}
        </div>
      </div>

      {error && (
        <div className="flex items-center gap-2 rounded-lg bg-red-50 border border-red-200 px-4 py-3 text-sm text-red-700">
          <AlertCircle size={18} className="shrink-0" />
          <span>Datenfelder konnten nicht geladen werden</span>
        </div>
      )}

      <div className="card overflow-hidden p-0">
        {isLoading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 size={24} className="animate-spin text-gray-400" />
          </div>
        ) : filteredFields.length === 0 ? (
          <div className="py-12 text-center">
            <Database size={48} className="mx-auto text-gray-300 mb-3" />
            <p className="text-sm text-gray-500">
              {searchQuery || typeFilter || clusterFilter ? 'Keine Datenfelder gefunden' : 'Noch keine Datenfelder vorhanden'}
            </p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-gray-50 border-b border-gray-200 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  <th className="px-4 py-3">Name</th>
                  <th className="px-4 py-3">Schlüssel</th>
                  <th className="px-4 py-3">Typ</th>
                  <th className="px-4 py-3">Organisation</th>
                  <th className="px-4 py-3">Pflicht</th>
                  <th className="px-4 py-3">Personenbez.</th>
                  <th className="px-4 py-3">Sensibel</th>
                  <th className="px-4 py-3">Aktionen</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {filteredFields.map((field) => (
                  <tr key={field.id} className="hover:bg-gray-50">
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-1.5">
                        {(() => {
                          const assigned = (field.clusters ?? [])
                            .map((entry) => clusters.find((cluster) => cluster.id === entry.clusterId))
                            .filter((cluster): cluster is Cluster => Boolean(cluster));
                          const visible = assigned.slice(0, 3);
                          const remaining = assigned.slice(3);
                          return (
                            <>
                              {visible.map((cluster) => (
                                <span
                                  key={cluster.id}
                                  className="h-3 w-3 shrink-0 rounded-full border border-gray-200 ring-1 ring-black/10"
                                  style={{ backgroundColor: cluster.color || '#9ca3af' }}
                                  title={cluster.name}
                                  aria-hidden="true"
                                />
                              ))}
                              {remaining.length > 0 && (
                                <span
                                  className="text-xs text-gray-500"
                                  title={remaining.map((cluster) => cluster.name).join(', ')}
                                >
                                  +{remaining.length}
                                </span>
                              )}
                            </>
                          );
                        })()}
                        <p className="font-medium text-gray-900">{field.label || field.name}</p>
                      </div>
                      <p className="text-xs text-gray-500">{field.name}</p>
                    </td>
                    <td className="px-4 py-3">
                      <code className="text-xs bg-gray-100 px-1.5 py-0.5 rounded text-gray-700">
                        {field.key}
                      </code>
                    </td>
                    <td className="px-4 py-3">
                      <span className={fieldTypeColors[field.type]}>
                        {fieldTypeLabels[field.type]}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-gray-600">
                      {(() => { const orgs = Array.isArray(orgsPage) ? orgsPage : (orgsPage?.data ?? []); const org = orgs.find(o => o.id === field.organizationId); return org?.name ?? '—'; })()}
                    </td>
                    <td className="px-4 py-3">
                      {field.isRequired ? (
                        <span className="text-red-500 font-medium">Ja</span>
                      ) : (
                        <span className="text-gray-400">Nein</span>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      {field.hasPersonReference ? (
                        <span className="text-orange-500 font-medium">Ja</span>
                      ) : (
                        <span className="text-gray-400">Nein</span>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      {field.isSensitive ? (
                        <span className="text-red-600 font-medium">Ja</span>
                      ) : (
                        <span className="text-gray-400">Nein</span>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-1">
                        <button onClick={() => handleEdit(field)}
                          className="btn-ghost p-1.5" title="Bearbeiten"
                          aria-label={`${field.name} bearbeiten`}>
                          <Edit2 size={14} />
                        </button>
                        <button onClick={() => handleDelete(field)}
                          className="btn-ghost p-1.5 text-red-500 hover:text-red-700 hover:bg-red-50" title="Löschen"
                          aria-label={`${field.name} löschen`}>
                          <Trash2 size={14} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

        </div>
      ) : (
        <div className="space-y-6">
          <div className="flex items-center gap-3">
            <Layers size={28} className="text-primary" />
            <div>
              <h1 className="text-2xl font-bold text-gray-900">Cluster</h1>
              <p className="text-sm text-gray-500">Pakete für Datenfelder und Fragebögen verwalten</p>
            </div>
          </div>

          {/* Anlegen / Bearbeiten */}
          <div className="card space-y-4">
            <h2 className="text-lg font-semibold text-gray-900">
              {editingClusterId ? 'Cluster bearbeiten' : 'Neuer Cluster'}
            </h2>

            {clusterError && (
              <div className="flex items-center gap-2 rounded-lg bg-red-50 border border-red-200 px-4 py-3 text-sm text-red-700">
                <AlertCircle size={18} className="shrink-0" />
                <span>{clusterError}</span>
              </div>
            )}

            <form onSubmit={handleClusterSubmit} className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label htmlFor="cluster-name" className="label">Name</label>
                  <input id="cluster-name" type="text" className="input-field" required
                    value={clusterForm.name}
                    onChange={(e) => setClusterForm({ ...clusterForm, name: e.target.value })} />
                </div>
                <div>
                  <label htmlFor="cluster-color" className="label">Farbe</label>
                  <input id="cluster-color" type="color" className="input-field h-[42px] cursor-pointer p-1"
                    value={clusterForm.color}
                    onChange={(e) => setClusterForm({ ...clusterForm, color: e.target.value })} />
                </div>
                <div className="md:col-span-2">
                  <label htmlFor="cluster-description" className="label">Beschreibung</label>
                  <input id="cluster-description" type="text" className="input-field"
                    value={clusterForm.description}
                    onChange={(e) => setClusterForm({ ...clusterForm, description: e.target.value })} />
                </div>
                <div className="md:col-span-2">
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input type="checkbox"
                      className="h-4 w-4 rounded border-gray-300 text-primary focus:ring-primary"
                      checked={clusterForm.visibleToChildren}
                      onChange={(e) => setClusterForm({ ...clusterForm, visibleToChildren: e.target.checked })} />
                    <span className="text-sm text-gray-700">Auch für untergeordnete Organisationen sichtbar</span>
                  </label>
                </div>
              </div>
              <div className="flex justify-end gap-3">
                {editingClusterId && (
                  <button type="button" onClick={resetClusterForm} className="btn-secondary">Abbrechen</button>
                )}
                <button type="submit" disabled={saveClusterMutation.isPending} className="btn-primary">
                  {saveClusterMutation.isPending ? (
                    <><Loader2 size={16} className="animate-spin" /> Speichern...</>
                  ) : (
                    editingClusterId ? 'Speichern' : 'Erstellen'
                  )}
                </button>
              </div>
            </form>
          </div>

          {/* Liste */}
          <div className="card overflow-hidden p-0">
            {clustersLoading ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 size={24} className="animate-spin text-gray-400" />
              </div>
            ) : clusters.length === 0 ? (
              <div className="py-12 text-center">
                <Layers size={48} className="mx-auto text-gray-300 mb-3" />
                <p className="text-sm text-gray-500">Noch keine Cluster vorhanden</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="bg-gray-50 border-b border-gray-200 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      <th className="px-4 py-3">Name</th>
                      <th className="px-4 py-3">Beschreibung</th>
                      <th className="px-4 py-3">Sichtbarkeit</th>
                      <th className="px-4 py-3">Aktionen</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {clusters.map((cluster) => (
                      <tr key={cluster.id} className="hover:bg-gray-50">
                        <td className="px-4 py-3">
                          <div className="flex items-center gap-2">
                            <span
                              className="h-3 w-3 shrink-0 rounded-full border border-gray-200"
                              style={{ backgroundColor: cluster.color || '#9ca3af' }}
                              aria-hidden="true"
                            />
                            <span className="font-medium text-gray-900">{cluster.name}</span>
                          </div>
                        </td>
                        <td className="px-4 py-3 text-gray-600">
                          {cluster.description || '—'}
                        </td>
                        <td className="px-4 py-3 text-gray-600">
                          {cluster.visibleToChildren
                            ? 'Auch untergeordnete Organisationen'
                            : 'Nur eigene Organisation'}
                        </td>
                        <td className="px-4 py-3">
                          {isOwnCluster(cluster) ? (
                            <div className="flex items-center gap-1">
                              <button onClick={() => openClusterEdit(cluster)}
                                className="btn-ghost p-1.5" title="Bearbeiten"
                                aria-label={`${cluster.name} bearbeiten`}>
                                <Edit2 size={14} />
                              </button>
                              <button onClick={() => handleClusterDelete(cluster)}
                                className="btn-ghost p-1.5 text-red-500 hover:text-red-700 hover:bg-red-50" title="Löschen"
                                aria-label={`${cluster.name} löschen`}>
                                <Trash2 size={14} />
                              </button>
                            </div>
                          ) : (
                            <span className="text-xs text-gray-400">Von übergeordneter Organisation</span>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      )}

      <FieldFormModal
        key={`${editField?.id || 'new'}-${formVersion}`}
        isOpen={showModal}
        onClose={handleCloseModal}
        editField={editField}
      />
      <AiBotWidget
        page="data-fields"
        active={showModal && !editField}
        onCreated={handleBotCreated}
      />
    </div>
  );
}
